import type { FactRelationship } from '../types';

export function wouldCreateFactRelationshipCycle(
  relationships: FactRelationship[],
  parentFactId: string,
  childFactId: string,
): boolean {
  if (parentFactId === childFactId) return true;

  const childrenByParent = new Map<string, string[]>();
  relationships.forEach((relationship) => {
    const children = childrenByParent.get(relationship.parent_fact_id) ?? [];
    children.push(relationship.child_fact_id);
    childrenByParent.set(relationship.parent_fact_id, children);
  });

  const pending = [childFactId];
  const visited = new Set<string>();
  while (pending.length > 0) {
    const current = pending.pop()!;
    if (current === parentFactId) return true;
    if (visited.has(current)) continue;
    visited.add(current);
    pending.push(...(childrenByParent.get(current) ?? []));
  }

  return false;
}

export function relationshipTouchesFact(relationship: FactRelationship, factId: string): boolean {
  return relationship.parent_fact_id === factId || relationship.child_fact_id === factId;
}

export type FactHierarchyMoveMode = 'individual' | 'group';

export function reparentFactHierarchy(
  relationships: FactRelationship[],
  factId: string,
  newParentFactId: string | null,
  mode: FactHierarchyMoveMode,
): FactRelationship[] {
  if (newParentFactId === factId) return relationships;

  const incoming = relationships.filter(relationship => relationship.child_fact_id === factId);
  if (newParentFactId === null && incoming.length === 0) return relationships;
  if (newParentFactId !== null && incoming.length === 1 && incoming[0]?.parent_fact_id === newParentFactId) {
    return relationships;
  }

  let next = relationships.filter(relationship => relationship.child_fact_id !== factId);
  if (mode === 'individual') {
    next = next.filter(relationship => relationship.parent_fact_id !== factId);
  }

  if (newParentFactId === null) return next;
  if (wouldCreateFactRelationshipCycle(next, newParentFactId, factId)) return relationships;

  return [
    ...next,
    {
      id: `factrel_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      parent_fact_id: newParentFactId,
      child_fact_id: factId,
      description: '',
    },
  ];
}
